import React, { useState, useCallback, useEffect } from "react";
import { Parser } from "json2csv";
import PropTypes from "prop-types";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import "@inovua/reactdatagrid-community/index.css";
import getLayout from "./DataGridLayouts";

const DataGrid = ({ data, options, layout_name, value_id, form_id, read_only }) => {
  const [dataSource, setDataSource] = useState(data);
  const [helpOpen, setHelpOpen] = useState(false);
  const [isUntouched, setUntouched] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridRef, setGridRef] = useState(null);
  const [footer, setFooter] = useState(null);

  const { columns, filterValue, defaultSortInfo } = getLayout(layout_name, { options });

  useEffect(() => {
    const getTotalPerc = (colName) => {
      let total = 0;
      dataSource.forEach((row) => {
        if (row[colName] != null) {
          total += parseFloat(row[colName]); 
        }
      });
      return total;
    };

    const colorFromPerc = (perc) => {
      if (perc == 100) {
        return 'green'
      } else {
        return 'red'
      }
    };

    const generateFooter = () => {
      const regExText = /[^a-zA-Z\s]+/;
      const footers = columns.filter((col) => col.defaultVisible != false)
        .map((col) => {
          if (col.showFooter) {
            const perc = getTotalPerc(col.name);
            return (
              <div key={col.name} className={`p-2 flex justify-center text-${colorFromPerc(perc)}-600`}>
                {col.header.replace(regExText, '')} Total: {perc}%
              </div>
            );
          }
          return (<div key={col.name}></div>);
        });

      return (
        <div className={`grid gap-4 grid-cols-${footers.length}`}>
          {footers}
        </div>
      );
    };

    if (columns.filter((col) => col.showFooter).length > 0) {
      setFooter(generateFooter());
    }
  }, [dataSource]);

  const downloadBlob = (blob, fileName = `${layout_name}-${Date.now()}.csv`) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.position = "absolute";
    link.style.visibility = "hidden";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const exportCSV = () => {
    const columns = gridRef.current.visibleColumns.map(c => {
      if (c.csvName) {
        return {
          label: c.csvName,
          value: c.name
        }
      }
      return c.name
    });

    // Get the filters applied to the columns
    const filters = {};
    gridRef.current.computedFilterValue.forEach((filter) => filters[filter.name] = {
      value: filter.value,
      type: filter.type
    });

    // Filter the data based on the applied filters
    const filteredData = dataSource.filter((row) => {
      let matchesFilter = true;

      for (const key in filters) {
        if (filters[key].value) {
          const cellValue = row[key];

          switch (filters[key].type) {
            case "bool":    
              if (Boolean(cellValue) != Boolean(filters[key].value)) {
                return false;
              }
              break;
          
            default:
              if (!String(cellValue).toLowerCase().includes(String(filters[key].value).toLowerCase())) {
                return false;
              }
              break;
          }
        }
      }

      return matchesFilter;
    });

    const parser = new Parser({ fields: columns });
    const csv = parser.parse(filteredData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob);
  };

  const saveState = () => {
    const valueEl = document.getElementById(value_id);
    const formEl = document.getElementById(form_id);
    valueEl.value = JSON.stringify(dataSource);
    formEl.submit();
  };

  const addNewRow = () => {
    const newRow = {
      id: Math.floor(Math.random() * 100) + dataSource.length,
      isNewRow: true
    };

    setDataSource([newRow, ...dataSource]);
  };

  const dupRow = () => {
    const data = [...dataSource];
    selectedRows.forEach((rowId) => {
      const newRow = { ...data.find(el => el.id == rowId) };
      newRow.id = Math.floor(Math.random() * 100) + dataSource.length;
      newRow.isNewRow = true;
      data.unshift(newRow);
    });

    setDataSource(data);
    setUntouched(false);
  };

  const deleteRows = () => {
    const data = [...dataSource];
    selectedRows.forEach((rowId) => {
      const dataRowIdx = data.findIndex(el => el.id == rowId);
      data.splice(dataRowIdx, 1);
    });

    setDataSource(data);
    setUntouched(false);
  };

  const onEditComplete = useCallback(({ value, columnId, rowId }) => {
    const data = [...dataSource];
    const dataRowIdx = data.findIndex(el => el.id == rowId);
    data[dataRowIdx][columnId] = value;

    setDataSource(data);
    setUntouched(false);
  }, [dataSource]);

  const onSelectionChange = useCallback(({ selected: selectedMap, data }) => {
    const newSelected = Object.keys(selectedMap).map(id => id * 1)

    setSelectedRows(newSelected);
  }, []);

  const gridStyle = { minHeight: "70vh" };

  return (
    <>
      <div className="flex justify-between py-2">
        <div className="grid gap-4 grid-cols-4">
          <button id="save-changes" disabled={isUntouched || read_only} className={`primary-btn items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:text-white focus:text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${(isUntouched || read_only) && "opacity-50 cursor-not-allowed"}`} onClick={saveState}>
            Save changes
          </button>

          <button id="new-row" className={`secondary-btn ${read_only && "opacity-50 cursor-not-allowed"}`} disabled={read_only} onClick={addNewRow}>
            Add New Row
          </button>

          <button id="dup-row" className={`secondary-btn ${read_only && "opacity-50 cursor-not-allowed"}`} disabled={read_only} onClick={dupRow}>
            Duplicate Row(s)
          </button>

          <button id="delete-rows" className={`secondary-btn ${(selectedRows.length == 0 || read_only) && "opacity-50 cursor-not-allowed"}`} disabled={selectedRows.length == 0 || read_only} onClick={deleteRows}>
            Delete Row(s)
          </button>
        </div>

        <button id="export-csv" className="secondary-btn" onClick={exportCSV}>
          Export CSV
        </button>
      </div>

      <ReactDataGrid
        style={gridStyle}
        columns={columns}
        defaultFilterValue={filterValue}
        defaultSortInfo={defaultSortInfo}
        onEditComplete={onEditComplete}
        onSelectionChange={onSelectionChange}
        dataSource={dataSource}
        editable={!read_only}
        handle={setGridRef}
        enableSelection
        multiSelect
        pagination
      />

      {footer}

      <div onMouseEnter={() => !read_only && setHelpOpen(true)} onMouseLeave={() => !read_only && setHelpOpen(false)} className={`mt-3 p-2 bg-gray-800 items-center text-sm text-gray-300 leading-none lg:rounded-full flex lg:inline-flex overflow-hidden ${helpOpen ? 'max-h-40' : 'max-h-9'} transition-all duration-500 ease-in-out`}>
        <span className="flex rounded-full bg-indigo-600 text-white px-2 py-1 text-sm font-bold mr-3">{read_only ? "Read only" : "Table Features"}</span>
        {helpOpen ?
          <ul className="text-sm list-disc p-2">
            <li>Double click cell to edit.</li>
            <li>Shift + Click after selecting first row, to select multiple rows.</li>
            <li>Control + Click to select multiple individual rows.</li>
          </ul> :
          <p className="text-sm p-2">{read_only ? "Table is in read only mode, no editing." : "Hover here to open help!"}</p>
        }
      </div>
    </>
  );
};

DataGrid.propTypes = {
  data: PropTypes.array,
};

export default DataGrid;
